import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Download as DownloadIcon, Loader2, X as CloseIcon } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { downloadActionI18nKey } from "../attachment-download-state.js";
import {
  attachmentDownloadFilename,
  downloadAttachmentFromSource,
  type AttachmentMediaSource,
} from "../attachment-media-actions.js";
import { shouldCloseImagePreviewOnBackdropPointerDown } from "../message-image-preview-logic.js";
import { useAttachmentDownload } from "../use-attachment-download.js";

export function MessageImagePreview({
  open,
  onOpenChange,
  imageUrl,
  alt,
  fileName,
  mimeType,
  organizationId,
  attachmentId,
  ensureUrl,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  alt: string;
  fileName?: string | null;
  mimeType?: string | null;
  organizationId?: number;
  attachmentId?: string;
  ensureUrl?: () => Promise<string | null>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const download = useAttachmentDownload();

  const resolveMediaSource = useCallback(async (): Promise<AttachmentMediaSource | null> => {
    if (organizationId != null && attachmentId) {
      return { kind: "stored", organizationId, attachmentId };
    }
    const url = ensureUrl ? await ensureUrl() : imageUrl;
    if (!url) {
      return null;
    }
    return { kind: "url", url };
  }, [attachmentId, ensureUrl, imageUrl, organizationId]);

  const handleDownload = useCallback(async () => {
    await download.run(async () => {
      const source = await resolveMediaSource();
      if (!source) {
        throw new Error("download_failed");
      }
      await downloadAttachmentFromSource(
        source,
        attachmentDownloadFilename(fileName, mimeType, "image"),
      );
    });
  }, [download, fileName, mimeType, resolveMediaSource]);

  const handleOverlayPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        shouldCloseImagePreviewOnBackdropPointerDown(event.target, event.currentTarget)
      ) {
        onOpenChange(false);
      }
    },
    [onOpenChange],
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[var(--z-alert-overlay)] bg-ink/85 animate-overlay-in data-[state=open]:animate-overlay-in"
          onPointerDown={handleOverlayPointerDown}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-0 z-[var(--z-alert)] flex flex-col outline-none pointer-events-none",
            "data-[state=open]:animate-overlay-in",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {t("inbox.media.preview.title", { label: alt })}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {t("inbox.media.preview.description")}
          </DialogPrimitive.Description>

          <div
            className="pointer-events-auto flex items-center justify-end gap-2 px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]"
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="border border-line/40 bg-ink/35 text-surface-elevated hover:bg-ink/50"
              disabled={download.disabled}
              onClick={() => void handleDownload()}
            >
              {download.status === "downloading" ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <DownloadIcon className="size-4" aria-hidden="true" />
              )}
              {t(downloadActionI18nKey(download.status))}
            </Button>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="border border-line/40 bg-ink/35 text-surface-elevated hover:bg-ink/50"
                aria-label={t("inbox.media.preview.close")}
              >
                <CloseIcon className="size-4" aria-hidden="true" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div
            className="pointer-events-none flex min-h-0 flex-1 items-center justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          >
            <img
              src={imageUrl}
              alt={alt}
              className="pointer-events-auto max-h-[min(82dvh,100%)] max-w-[min(96vw,100%)] object-contain"
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
